import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { GlassCard } from '../glass-card/glass-card';
import { WaveformBars } from '../../atoms/waveform-bars/waveform-bars';

export type AudioPlayerVariant = 'card' | 'inline';

/**
 * AudioPlayer — reusable audio playback molecule with waveform visualisation.
 *
 * @example
 * <!-- Standalone card (memory detail) -->
 * <om-audio-player [audioUrl]="url" label="AUDIO MEMORY" />
 *
 * @example
 * <!-- Inline inside another card -->
 * <om-audio-player [audioUrl]="url" variant="inline" />
 */
@Component({
  selector: 'om-audio-player',
  imports: [NgTemplateOutlet, GlassCard, WaveformBars],
  templateUrl: './audio-player.html',
  styleUrl: './audio-player.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioPlayer implements OnInit {
  readonly audioUrl = input.required<string>();
  readonly label = input<string>('');
  /** 'card' wraps in glass-card (default). 'inline' renders bare layout. */
  readonly variant = input<AudioPlayerVariant>('card');

  private readonly destroyRef = inject(DestroyRef);
  private audio: HTMLAudioElement | null = null;

  readonly state = signal<'idle' | 'playing' | 'finished'>('idle');
  readonly currentTime = signal(0);
  readonly duration = signal(0);
  readonly waveformUrl = signal<string | null>(null);

  readonly isPlaying = computed(() => this.state() === 'playing');
  readonly isFinished = computed(() => this.state() === 'finished');

  readonly playIcon = computed(() => {
    switch (this.state()) {
      case 'playing': return 'pause';
      case 'finished': return 'replay';
      default: return 'play_arrow';
    }
  });

  readonly formattedCurrent = computed(() => this.formatTime(this.currentTime()));
  readonly formattedDuration = computed(() => this.formatTime(this.duration()));

  readonly normalizedProgress = computed(() => {
    const d = this.duration();
    return d > 0 ? this.currentTime() / d : 0;
  });

  ngOnInit(): void {
    this.waveformUrl.set(this.audioUrl());
    this.audio = new Audio(this.audioUrl());

    this.audio.onloadedmetadata = () => {
      if (this.audio && isFinite(this.audio.duration)) {
        this.duration.set(this.audio.duration);
      }
    };

    this.audio.ontimeupdate = () => {
      if (this.audio) this.currentTime.set(this.audio.currentTime);
    };

    this.audio.onended = () => {
      this.state.set('finished');
      this.currentTime.set(this.duration());
    };

    this.audio.onpause = () => {
      if (this.state() === 'playing') this.state.set('idle');
    };

    this.destroyRef.onDestroy(() => {
      if (this.audio) {
        this.audio.pause();
        this.audio = null;
      }
    });
  }

  toggle(): void {
    if (!this.audio) return;

    if (this.isPlaying()) {
      this.audio.pause();
    } else {
      if (this.isFinished()) {
        this.audio.currentTime = 0;
        this.currentTime.set(0);
      }
      this.audio.play().then(() => {
        this.state.set('playing');
      }).catch(() => {
        this.state.set('idle');
      });
    }
  }

  onSeek(ratio: number): void {
    if (!this.audio || !this.duration()) return;
    const time = Math.max(0, Math.min(this.duration(), ratio * this.duration()));
    this.audio.currentTime = time;
    this.currentTime.set(time);
    if (this.isFinished()) this.state.set('idle');
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
